import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle
} from "@/components/ui/alert-dialog";
import { Sword, Shield, BookOpen, Trash2, Plus, LogOut, Clock, Crown, Trophy } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { t, getStoredLang } from "@/utils/i18n";
import SettingsPanel from "@/components/game/SettingsPanel";
import ambientMusic from "@/utils/AmbientMusic";
import NyxMascot from "@/components/game/NyxMascot";

export default function MainMenu() {
  const { user, logout, token, API } = useAuth();
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lang, setLang] = useState(getStoredLang());
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { loadGames(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { ambientMusic.play('menu'); }, []);
  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const res = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
        setIsAdmin(!!res.data?.is_admin);
      } catch {}
    };
    if (token) checkAdmin();
  }, [token, API]);

  const loadGames = async () => {
    try {
      const res = await axios.get(`${API}/games`, { headers: { Authorization: `Bearer ${token}` } });
      setGames(res.data);
    } catch (e) {
      toast.error("Failed to load saved games");
    } finally {
      setLoading(false);
    }
  };

  const openDeleteDialog = (gameId, gameName, e) => {
    e.stopPropagation();
    e.preventDefault();
    setDeleteTarget({ id: gameId, name: gameName });
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/game/${deleteTarget.id}`, { headers: { Authorization: `Bearer ${token}` } });
      setGames(g => g.filter(x => x.id !== deleteTarget.id));
      toast.success(t(lang, 'confirm_delete') + "!");
    } catch {
      toast.error("Failed to delete");
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const classIcons = { warrior: Sword, mage: BookOpen, rogue: Shield, ranger: Sword, necromancer: BookOpen };

  return (
    <div className="min-h-screen bg-[#0a0a0c] relative" data-testid="main-menu">
      <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0c] via-[#1a1a1d] to-[#0a0a0c]" />
      <div className="absolute top-20 right-20 w-64 h-64 bg-[#cfb53b] rounded-full opacity-[0.02] blur-[80px]" />

      <div className="relative z-10 max-w-4xl mx-auto px-3 sm:px-4 py-6 sm:py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8 sm:mb-12 gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <img
              src="/logo.png"
              alt="Arcane Chronicles"
              className="w-12 h-12 sm:w-16 sm:h-16 rounded-lg shadow-[0_0_20px_rgba(207,181,59,0.25)] shrink-0"
              data-testid="app-logo"
            />
            <div className="min-w-0">
              <h1 className="font-heading text-xl sm:text-3xl lg:text-4xl font-bold text-[#cfb53b] truncate">Arcane Chronicles</h1>
              <p className="text-[#94a3b8] mt-1 font-body text-xs sm:text-base truncate">{t(lang, 'welcome')}, {user?.username || "Adventurer"}</p>
            </div>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            {isAdmin && (
              <Button
                data-testid="admin-button"
                onClick={() => navigate("/admin")}
                variant="ghost"
                size="sm"
                className="text-[#cfb53b] hover:bg-[#cfb53b]/10"
              >
                <Crown size={18} />
                <span className="hidden sm:inline ml-1.5">Admin</span>
              </Button>
            )}
            <SettingsPanel onLangChange={setLang} />
            <Button
              data-testid="logout-button"
              onClick={() => { logout(); navigate("/auth"); }}
              variant="ghost"
              size="sm"
              className="text-[#94a3b8] hover:text-[#cfb53b] hover:bg-[#cfb53b]/10"
            >
              <LogOut size={18} />
              <span className="hidden sm:inline ml-2">{t(lang, 'logout')}</span>
            </Button>
          </div>
        </div>

        {/* New Game Button */}
        <Button
          data-testid="new-game-button"
          onClick={() => navigate("/create")}
          className="w-full mb-3 sm:mb-4 bg-[#cfb53b] hover:bg-[#d4af37] text-[#0a0a0c] font-heading font-bold text-base sm:text-xl py-4 sm:py-6 rounded-lg transition-all hover:shadow-[0_0_30px_rgba(207,181,59,0.3)] group"
        >
          <Plus size={20} className="mr-2 sm:mr-3 group-hover:rotate-90 transition-transform duration-300" />
          {t(lang, 'new_adventure')}
        </Button>

        {/* Hall of Stories — public showcase entry */}
        <Button
          data-testid="hall-of-stories-btn"
          onClick={() => navigate("/hall")}
          variant="outline"
          className="w-full mb-8 sm:mb-10 border-[#cfb53b]/30 text-[#cfb53b] hover:bg-[#cfb53b]/10 hover:border-[#cfb53b]/60 font-heading text-sm sm:text-base py-3 sm:py-4 rounded-lg transition-all"
        >
          <Trophy size={18} className="mr-2 text-[#cfb53b]" />
          {lang.startsWith("it") ? "Hall of Stories — Sfoglia le saghe" : "Hall of Stories — Browse the sagas"}
        </Button>

        {/* Saved Games */}
        <div>
          <h2 className="font-heading text-base sm:text-xl text-[#e2e8f0] mb-3 sm:mb-4 flex items-center gap-2">
            <BookOpen size={18} className="text-[#cfb53b]" /> {t(lang, 'saved_adventures')}
          </h2>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-2 border-[#cfb53b] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : games.length === 0 ? (
            <div className="text-center py-12 sm:py-16 border border-dashed border-[#cfb53b]/20 rounded-lg">
              <BookOpen size={40} className="mx-auto text-[#cfb53b]/30 mb-3 sm:mb-4" />
              <p className="text-[#94a3b8] font-body text-base sm:text-lg">{t(lang, 'no_games')}</p>
              <p className="text-[#64748b] text-xs sm:text-sm mt-1">{t(lang, 'start_first')}</p>
            </div>
          ) : (
            <div className="grid gap-3 sm:gap-4 stagger-children">
              {games.map((g) => {
                const ClassIcon = classIcons[g.character?.class_type] || Sword;
                return (
                  <div
                    key={g.id}
                    data-testid={`game-card-${g.id}`}
                    onClick={() => navigate(`/game/${g.id}`)}
                    className="bg-[#1a1a1d]/80 border border-[#cfb53b]/15 rounded-lg p-3 sm:p-5 cursor-pointer hover:border-[#cfb53b]/40 hover:bg-[#1a1a1d] transition-all group animate-fadeInUp"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-[#cfb53b]/10 flex items-center justify-center group-hover:bg-[#cfb53b]/20 transition-colors shrink-0">
                          <ClassIcon size={20} className="text-[#cfb53b] sm:w-6 sm:h-6" />
                        </div>
                        <div className="min-w-0">
                          <h3 className="font-heading text-sm sm:text-lg text-[#e2e8f0] group-hover:text-[#cfb53b] transition-colors truncate">
                            {g.character?.name || "Unknown"}
                          </h3>
                          <div className="flex items-center gap-1.5 sm:gap-3 text-xs sm:text-sm text-[#94a3b8] flex-wrap">
                            <span className="capitalize">{g.character?.class_type}</span>
                            <span className="text-[#cfb53b]/40 hidden sm:inline">|</span>
                            <span className="capitalize hidden sm:inline">{g.setting}</span>
                            <span className="text-[#cfb53b]/40">|</span>
                            <span>{t(lang, 'level')} {g.character?.level || 1}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 sm:gap-3 shrink-0">
                        <div className="text-right hidden sm:block">
                          <div className="text-xs text-[#64748b] flex items-center gap-1">
                            <Clock size={12} />
                            {new Date(g.updated_at).toLocaleDateString()}
                          </div>
                          <div className="text-xs text-[#8b0000]">
                            {t(lang, 'hp')}: {g.character?.hp}/{g.character?.max_hp}
                          </div>
                        </div>
                        <Button
                          data-testid={`delete-game-${g.id}`}
                          variant="ghost"
                          size="sm"
                          onClick={(e) => openDeleteDialog(g.id, g.character?.name, e)}
                          className="text-[#64748b] hover:text-[#8b0000] hover:bg-[#8b0000]/10 h-8 w-8 p-0"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </div>
                    {/* Mobile-only HP row */}
                    <div className="sm:hidden flex items-center justify-between mt-2 pt-2 border-t border-[#2d3748]/50 text-xs text-[#64748b]">
                      <span className="capitalize">{g.setting}</span>
                      <span className="text-[#8b0000]">{t(lang, 'hp')}: {g.character?.hp}/{g.character?.max_hp}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent className="bg-[#1a1a1d] border border-[#8b0000]/30 max-w-[90vw] sm:max-w-lg" data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-[#e2e8f0] text-lg">
              {t(lang, 'delete_title')}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#94a3b8]">
              {t(lang, 'delete_desc')}
              {deleteTarget && (
                <span className="block mt-2 font-heading text-[#cfb53b]">"{deleteTarget.name}"</span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="delete-cancel"
              className="bg-transparent border-[#2d3748] text-[#94a3b8] hover:bg-[#2d3748] hover:text-[#e2e8f0]"
            >
              {t(lang, 'cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-confirm"
              onClick={confirmDelete}
              disabled={deleting}
              className="bg-[#8b0000] hover:bg-[#a00000] text-white border-0"
            >
              {deleting ? (
                <span className="flex items-center gap-2">
                  <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Trash2 size={14} />
                  {t(lang, 'confirm_delete')}
                </span>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Legal footer */}
      <footer className="mt-10 py-6 border-t border-[#2d3748] text-center" data-testid="legal-footer">
        <div className="flex flex-wrap justify-center gap-x-3 gap-y-1 text-[10px] sm:text-xs text-[#64748b] px-4">
          <Link to="/about" className="hover:text-[#cfb53b] transition-colors" data-testid="footer-about">About</Link>
          <span className="text-[#2d3748]">·</span>
          <Link to="/privacy" className="hover:text-[#cfb53b] transition-colors" data-testid="footer-privacy">Privacy</Link>
          <span className="text-[#2d3748]">·</span>
          <Link to="/terms" className="hover:text-[#cfb53b] transition-colors" data-testid="footer-terms">{(lang || "en").startsWith("it") ? "Termini" : "Terms"}</Link>
          <span className="text-[#2d3748]">·</span>
          <Link to="/cookies" className="hover:text-[#cfb53b] transition-colors" data-testid="footer-cookies">Cookie</Link>
          <span className="text-[#2d3748]">·</span>
          <Link to="/contact" className="hover:text-[#cfb53b] transition-colors" data-testid="footer-contact">{(lang || "en").startsWith("it") ? "Contatti" : "Contact"}</Link>
        </div>
        <p className="mt-2 text-[10px] text-[#64748b]/70">© {new Date().getFullYear()} Arcane Chronicles</p>
      </footer>
      <NyxMascot />
    </div>
  );
}
